import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { useAppDispatch, useAppSelector } from "app/hooks";
import Dialog from "components/dialog";
import {
  AMBIENT_SCRIBE_GETTING_STARTED,
  AMBIENT_SCRIBE_PAUSED_FEATURE,
  AMBIENT_SCRIBE_TEMPLATE_TYPES
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import ManageScribeTemplatesButton from "features/medical-scribe/components/manage-scribe-templates-button.component";
import TrialStatusBanner from "features/medical-scribe/components/trial-status-banner.component";
import ScribeGuideButton from "features/medical-scribe/getting-started/scribe-guide-button.component";
import useScribeGuideDialog from "features/medical-scribe/getting-started/use-scribe-guide-dialog.hook";
import ViewScribeDetails from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details";
import { useMedicalScribe } from "features/medical-scribe/use-medical-scribe.hook";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import {
  showInsertScribe,
  showInsertScribeForChartNoteId
} from "../patient-chart-notes.slice";
import InsertScribeActions, {
  type InsertScribeFormValues
} from "./insert-scribe-actions.component";
import InsertScribeEmptyPanel from "./insert-scribe-empty-panel.component";
import InsertScribeListPanel from "./insert-scribe-list-panel.component";
import useInsertScribeDetailsMode from "./use-insert-scribe-details-mode.hook";
import useInsertScribeLayout, {
  STACK_SX,
  VIEW_SCRIBE_DETAILS_SX
} from "./use-insert-scribe-layout.hook";
import useInsertScribeSource from "./use-insert-scribe-source.hook";
import useInsertScribeSubmit from "./use-insert-scribe-submit.hook";
import { isAudiologyInsertMode } from "./utils";

const DEFAULT_VALUES: InsertScribeFormValues = {
  needReplace: false,
  isCollapsed: false
};

const MAIN_CONTAINER_SX = {
  width: "100%",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
} as const;

const CONTENT_WRAPPER_SX = {
  flex: 1,
  overflow: "hidden",
  pb: 2
} as const;

type InsertScribeDialogProps = {
  patientId: number;
};

export default function InsertScribeDialog({
  patientId
}: InsertScribeDialogProps) {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;
  const isGettingStartedOn = flags[AMBIENT_SCRIBE_GETTING_STARTED] === true;
  const { openGuide, guideDialog } = useScribeGuideDialog("patient_page");
  // Unknown until the list's first read settles, so the right panel never flashes a wrong prompt.
  const [hasRecordings, setHasRecordings] = useState<boolean>();

  const { editorRef, insertScribeSelectionBookmarkRef } = useMedicalScribe();
  const insertScribeState = useAppSelector(showInsertScribeForChartNoteId);
  const audiologyReportId = insertScribeState.parentId;
  const isAudiologyMode = isAudiologyInsertMode({
    isTemplateTypesEnabled: Boolean(flags[AMBIENT_SCRIBE_TEMPLATE_TYPES]),
    templateType: insertScribeState.templateType,
    audiologyReportId
  });

  const { handleSubmit, control, reset, watch, formState } =
    useForm<InsertScribeFormValues>({
      mode: "onChange",
      defaultValues: DEFAULT_VALUES
    });
  const [isCollapsed] = watch<["isCollapsed"]>(["isCollapsed"]);

  const [selectedScribe, setSelectedScribe] = useState<AmbientScribe | null>(
    null
  );
  const { isEditing, resetToView, enterEdit, detailsProps } =
    useInsertScribeDetailsMode({ isPausedFeatureEnabled });
  const isEditScribePanel =
    isPausedFeatureEnabled && isEditing && Boolean(selectedScribe);
  const { isMobile, isTablet, minWidth, sx, paperSx } = useInsertScribeLayout({
    isCollapsed,
    isEditScribePanel
  });

  // Kept apart from selectedScribe, so SelectMedicalScribeField's sync-back never reverts the
  // selection to the list's copy.
  const { effectiveScribe, tabs } = useInsertScribeSource({
    selectedScribe,
    templateType: insertScribeState.templateType,
    audiologyReportId
  });

  const handleClose = useCallback(
    (event?: object, reason?: string) => {
      if (reason === "backdropClick") {
        return;
      }
      insertScribeSelectionBookmarkRef.current = null;
      reset(DEFAULT_VALUES);
      dispatch(showInsertScribe({ show: false }));
    },
    [dispatch, insertScribeSelectionBookmarkRef, reset]
  );

  const { hasEditor, hasContent, buttonLabel, insert, copyAndClose } =
    useInsertScribeSubmit({
      editorRef,
      insertScribeSelectionBookmarkRef,
      tabView: tabs.tabView,
      effectiveScribe,
      selectedScribeId: selectedScribe?.id,
      isAudiologyMode,
      audiologyReportId,
      onClose: handleClose
    });

  const handleFormSubmit = useCallback(() => {
    handleSubmit(({ needReplace }) => insert(needReplace))();
  }, [handleSubmit, insert]);

  const handleSelect = useCallback(
    (value: AmbientScribe | null) => {
      setSelectedScribe(value);
      if (isPausedFeatureEnabled) {
        resetToView();
      }
    },
    [isPausedFeatureEnabled, resetToView]
  );

  const handleOpenEditScribe = useCallback(
    (scribe: AmbientScribe) => {
      setSelectedScribe(scribe);
      enterEdit("sessions");
    },
    [enterEdit]
  );

  const isActionDisabled = !formState.isValid || !selectedScribe || !hasContent;
  const showActions =
    (!isPausedFeatureEnabled || !isEditing) &&
    (!isTablet || !isActionDisabled) &&
    Boolean(selectedScribe);

  return (
    <>
      <Dialog
        title={isPausedFeatureEnabled ? "Scribes" : "Select a Medical Scribe"}
        minWidth={minWidth}
        onClose={handleClose}
        open
        hideBackdrop={isCollapsed}
        disableEnforceFocus={isCollapsed}
        disableScrollLock={isCollapsed}
        dialogContentProps={{ sx: sx.dialogContent }}
        dialogTitleSx={sx.dialogTitle}
        paperContentSx={paperSx}
        isDraggable
        sx={sx.dialog}
        extraTitleButton={
          isGettingStartedOn ? (
            <Stack direction="row" alignItems="center" columnGap={1}>
              <ScribeGuideButton
                size="small"
                onClick={() => openGuide("insert_scribe_title")}
              />
              <ManageScribeTemplatesButton />
            </Stack>
          ) : (
            <ManageScribeTemplatesButton />
          )
        }
      >
        <TrialStatusBanner featureKey={AMBIENT_SCRIBE_FEATURE_KEY} />
        <Box sx={MAIN_CONTAINER_SX}>
          <Box sx={CONTENT_WRAPPER_SX}>
            <Stack direction={isTablet ? "column" : "row"} sx={STACK_SX}>
              <InsertScribeListPanel
                patientId={patientId}
                isCollapsed={isCollapsed}
                sx={sx.leftPanel}
                selected={selectedScribe}
                onSelect={handleSelect}
                onSelectedScribeSync={setSelectedScribe}
                onEdit={handleOpenEditScribe}
                isPausedFeatureEnabled={isPausedFeatureEnabled}
                isGettingStartedOn={isGettingStartedOn}
                openGuide={openGuide}
                onHasRecordingsChange={setHasRecordings}
              />
              <Box sx={sx.rightPanel}>
                {!selectedScribe ? (
                  <InsertScribeEmptyPanel
                    isGettingStartedOn={isGettingStartedOn}
                    isAudiologyMode={isAudiologyMode}
                    hasRecordings={hasRecordings}
                  />
                ) : (
                  <ViewScribeDetails
                    key={selectedScribe.id}
                    remoteMedicalScribeId={Number(selectedScribe.id)}
                    {...detailsProps}
                    onEditClose={handleClose}
                    onEditFinalized={resetToView}
                    tabs={tabs}
                    hideHeader={isCollapsed}
                    templateType={insertScribeState.templateType}
                    audiologyReportId={audiologyReportId}
                    sx={VIEW_SCRIBE_DETAILS_SX}
                  />
                )}
              </Box>
            </Stack>
          </Box>
          {showActions && (
            <InsertScribeActions
              control={control}
              isMobile={isMobile}
              isTablet={isTablet}
              hasEditor={hasEditor}
              label={buttonLabel}
              disabled={isActionDisabled}
              onSubmit={handleFormSubmit}
              onCopyAndClose={copyAndClose}
            />
          )}
        </Box>
      </Dialog>
      {/* Outside the draggable dialog's React tree, so events in the guide never reach it. */}
      {guideDialog}
    </>
  );
}
